import type {
  CandidateProfile,
  Evaluation,
  EvaluationRead,
  IAuthMember,
  ProfileDataResponse,
  PublicProfileState,
  PublishedResponse
} from '@/generated-api'
import { factory } from '../../../../factory-api/factory'
import type { UserPersonalData } from '@/generated-api/models/UserPersonalData'
import { defineStore } from 'pinia'

export const useUserStore = defineStore('user', () => {
  const personalData = ref<UserPersonalData>()
  const memberData = ref<ProfileDataResponse>()
  const member = ref({} as IAuthMember)

  const setMember = (newMember: IAuthMember) => {
    member.value = newMember
  }

  const clearMember = () => {
    member.value = {} as IAuthMember
  }

  const getPersonalData = async () => {
    try {
      const personalDataApi = await factory.getPersonalDataApi()
      const response = await personalDataApi.getPersonalData()

      setPersonalData(response)
    } catch (error) {
      console.error(error)
    }
  }

  const savePersonalData = async (personalData: UserPersonalData): Promise<boolean> => {
    try {
      const candidateProfile: CandidateProfile = {
        personalData: personalData,
        id: member.value?.id
      }
      const memberApi = await factory.getMemberApi()
      const response = await memberApi.updateProfile({
        candidateProfile: candidateProfile,
        onlyPersonalData: true
      })

      return response.success ?? false
    } catch (error) {
      return false
    }
  }

  const getMemberData = async () => {
    try {
      const memberApi = await factory.getMemberApi()
      const response = await memberApi.getProfileData()

      memberData.value = response
    } catch (error) {
      console.log(error)
    }
  }

  const saveContentViewLog = async (contentId: string, type: string) => {
    try {
      const memberApi = await factory.getMemberApi()

      const response = await memberApi.saveContentViewLog({
        contentViewLog: {
          userId: member.value?.id,
          contentId: contentId,
          contentType: type
        }
      })

      console.log(response)
    } catch (error) {
      console.log(error)
    }
  }

  const updateLinkedInLearningAccount = async (activated: boolean, learningLogin: string) => {
    const memberApi = await factory.getMemberApi()
    return await memberApi.updateLinkedinLearningAccount({
      linkedinLearningAccount: { activated: activated, linkedinLearningLogin: learningLogin }
    })
  }

  const deleteProfileImage = async () => {
    try {
      const api = await factory.getMemberApi()
      const response = await api.deleteProfileImage()
      console.log(response)
    } catch (error) {
      console.log(error)
    }
  }

  const getNetworkProfileState = async (): Promise<PublicProfileState> => {
    try {
      const memberApi = await factory.getMemberApi()

      const response = await memberApi.getCandidateNetworkProfileState({ id: member.value.id })

      return response
    } catch (error) {
      console.log(error)

      return {}
    }
  }

  const getJobProfileState = async (): Promise<PublicProfileState> => {
    try {
      const memberApi = await factory.getMemberApi()

      const response = await memberApi.getCandidateJobProfileState({ id: member.value.id })

      return response
    } catch (error) {
      console.log(error)

      return {}
    }
  }

  const toggleNetworkProfileStatus = async (toggleProfile: boolean): Promise<PublishedResponse> => {
    try {
      const memberApi = await factory.getMemberApi()

      //Publish text is needed, or else the endpoint wont save the profile status. The text is not used in modern therefore its just a string containing 'publishText'

      const response = await memberApi.toggleNetworkProfilePublishedState({
        toggleProfilePublicState: {
          id: member.value.id,
          publish: toggleProfile,
          publishText: 'publishText'
        }
      })

      return response
    } catch (error) {
      console.log(error)

      return {}
    }
  }

  const toggleJobProfileStatus = async (toggleProfile: boolean): Promise<PublishedResponse> => {
    try {
      const memberApi = await factory.getMemberApi()

      //Publish text is needed, or else the endpoint wont save the profile status. The text is not used in modern therefore its just a string containing 'publishText'

      const response = await memberApi.toggleJobProfilePublishedState({
        toggleProfilePublicState: {
          id: member.value.id,
          publish: toggleProfile,
          publishText: 'publishText'
        }
      })

      return response
    } catch (error) {
      console.log(error)

      return {}
    }
  }

  const setPersonalData = (data: UserPersonalData) => {
    personalData.value = data
  }

  const getCandidateEvaluations = async (): Promise<EvaluationRead[]> => {
    try {
      const memberApi = await factory.getMemberApi()
      const response = await memberApi.getCandidateEvaluations()

      return response
    } catch (error) {
      console.log(error)
      return []
    }
  }

  return {
    member,
    personalData,
    memberData,
    clearMember,
    setMember,
    getPersonalData,
    savePersonalData,
    getMemberData,
    saveContentViewLog,
    updateLinkedInLearningAccount,
    getNetworkProfileState,
    toggleNetworkProfileStatus,
    deleteProfileImage,
    getJobProfileState,
    toggleJobProfileStatus,
    setPersonalData,
    getCandidateEvaluations
  }
})
